import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigation,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { Suspense, lazy } from "react";
import { useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import nProgress from "nprogress";
import "nprogress/nprogress.css";
// Critical components that need to be loaded immediately
import Header from "./commons/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./commons/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import WhatsAppButton from "./components/WhatsAppButton";
import {
  initFacebookPixel,
  trackPageView,
  buildAdvancedMatching,
} from "./utils/facebookPixel";
import { getStoredPixelContact } from "./utils/tiktokPixel";
import logo from "./assets/logo.png";

// Capture Facebook Click ID (fbclid) from URL and store as _fbc cookie
// This improves Meta CAPI event match quality
function captureFacebookClickId() {
  if (typeof window === 'undefined') return;
  
  // Get fbclid from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const fbclid = urlParams.get('fbclid');
  
  if (!fbclid) return; // No fbclid in URL
  
  // Check if _fbc cookie already exists (don't overwrite)
  const existingFbc = document.cookie
    .split('; ')
    .find(row => row.startsWith('_fbc='));
  
  if (existingFbc) return; // Already captured
  
  // Format: fb.1.<timestamp>.<fbclid>
  const timestamp = Math.floor(Date.now() / 1000);
  const fbc = `fb.1.${timestamp}.${fbclid}`;
  
  // Set cookie (expires in 90 days, same as Meta Pixel)
  const expires = new Date();
  expires.setTime(expires.getTime() + 90 * 24 * 60 * 60 * 1000);
  document.cookie = `_fbc=${fbc}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  
  console.info('[Meta CAPI] Captured fbclid and set _fbc cookie:', fbc);
}

function runWhenIdle(callback, timeout = 5000) {
  if (typeof window === "undefined") return;

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(callback, { timeout });
    return;
  }

  window.setTimeout(callback, timeout);
}

// Lazy load all other components
const LandingPage = lazy(() => import("./pages/LandingPage"));
const Register = lazy(() => import("./pages/Register"));
const Login = lazy(() => import("./pages/login"));
const ProductDetailsPage = lazy(() => import("./components/ProductDetailsPage"));
const EmailVerify = lazy(() => import("./components/EmailVerify"));
const Checkout = lazy(() => import("./components/Checkout"));
const OrderSuccess = lazy(() => import("./components/OrderSuccess"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const Profile = lazy(() => import("./pages/ProfilePage"));
const About = lazy(() => import("./components/About"));
const Faq = lazy(() => import("./components/Faq"));
const ContactForm = lazy(() => import("./components/Contact"));
const ShippingInfo = lazy(() => import("./components/ShippingInfo"));
const ReturnsExchanges = lazy(() => import("./components/ReturnsExchanges"));
const TermsOfService = lazy(() => import("./components/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const AdminLayout = lazy(() => import("./admin/AdminLayout"));
const ProductList = lazy(() => import("./admin/ProductList"));
const ProductManagement = lazy(() => import("./admin/ProductManagement"));
const OrderList = lazy(() => import("./admin/OrderList"));
const Banners = lazy(() => import("./admin/Banners"));
const Review = lazy(() => import("./components/Review"));
const ProductPage = lazy(() => import("./components/ProductPage"));
const AnnouncementsPage = lazy(() => import("./admin/AnnouncementsPage"));
const SearchOverlay = lazy(() => import("./components/SearchOverlay"));
const AttarsPage = lazy(() => import("./pages/AttarsPage"));
const AttarDetailPage = lazy(() => import("./pages/AttarDetailPage"));
const AttarsList = lazy(() => import("./admin/AttarsList"));
const AttarForm = lazy(() => import("./admin/AttarForm"));
const AdminDashboard = lazy(() => import("./admin/AdminDashboard"));
const ProductUpload = lazy(() => import("./admin/ProductsUpload"));
const OurStory = lazy(() => import("./pages/OurStory"));
const MainCategoryPage = lazy(() => import("./pages/MainCategoryPage"));
const AllProductsPage = lazy(() => import("./pages/AllProductsPage"));
const CategoryManagement = lazy(() => import("./admin/CategoryManagement"));
const ProductAttributeList = lazy(() => import("./admin/ProductAttributeList"));
const ProductReviewManagement = lazy(() => import("./admin/ProductReviewManagement"));
const TrackOrder = lazy(() => import("./components/TrackOrderPro"));

nProgress.configure({ showSpinner: false });

// Loading component for Suspense fallback — fixed overlay so logo doesn't jump
const LoadingSpinner = () => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#fff",
      zIndex: 99999,
    }}
  >
    <img
      src={logo}
      alt="ZAAR"
      className="logo-loading-beat"
      style={{ width: 140, height: "auto" }}
    />
  </div>
);

function RouteChangeHandler() {
  const location = useLocation();
  const prevLocation = useRef(location);

  useEffect(() => {
    // Capture fbclid on every route change (in case it's in URL)
    captureFacebookClickId();
    
    if (location !== prevLocation.current) {
      nProgress.start();
      prevLocation.current = location;
      
      // Track PageView for Meta Pixel on route change
      trackPageView(location.pathname);
    }

    const timeout = setTimeout(() => {
      nProgress.done();
    }, 500); // you can tweak this

    return () => {
      clearTimeout(timeout);
    };
  }, [location]);

  return null;
}

const App = () => {
  const [showApp, setShowApp] = useState(false);

  // Remove initial HTML loader and show app once React has mounted
  useEffect(() => {
    const loader = document.getElementById("initial-loader");
    if (loader) loader.remove();
    const t = setTimeout(() => setShowApp(true), 50);
    return () => clearTimeout(t);
  }, []);

  // Initialize Facebook Pixel after page load to avoid blocking LCP/TBT
  useEffect(() => {
    captureFacebookClickId();
    const init = () => {
      runWhenIdle(() => {
        // Manual Advanced Matching: pass known contact as 3rd fbq('init') arg
        const stored = getStoredPixelContact();
        const advancedMatching = buildAdvancedMatching({
          email: stored.email,
          phone: stored.phone,
        });
        initFacebookPixel(advancedMatching);
        trackPageView(window.location.pathname);
      }, 6000);
    };
    if (document.readyState === 'complete') {
      init();
    } else {
      window.addEventListener('load', init, { once: true });
    }
  }, []);

  // Show logo loader until app is ready (inline styles = no layout shift, no duplicate)
  if (!showApp) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fff",
          zIndex: 99999,
        }}
      >
        <img
          src={logo}
          alt="ZAAR"
          className="logo-loading-beat"
          style={{ width: 140, height: "auto" }}
        />
      </div>
    );
  }


  return (
    <>
          <WhatsAppButton />

      <SearchOverlay />

      <Header />
      <Toaster position="top-center" reverseOrder={false} />
      <RouteChangeHandler />
      <ScrollToTop />

      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route
            path="/admin"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="products" element={<ProductManagement />} />
            <Route path="products/upload" element={<ProductUpload />} />
            <Route path="orders" element={<OrderList />} />
            <Route path="banner" element={<Banners />} />
            <Route path="announcements" element={<AnnouncementsPage />} />
            <Route path="attars" element={<AttarsList />} />
            <Route path="attars/create" element={<AttarForm />} />
            <Route path="attars/edit/:id" element={<AttarForm />} />
            <Route path="categories" element={<CategoryManagement />} />
            <Route path="product-attributes" element={<ProductAttributeList />} />
            <Route path="product-reviews" element={<ProductReviewManagement />} />
          </Route>

          <Route path="/" element={<LandingPage />} />
          <Route path="/shop" element={<AllProductsPage />} />
          <Route path="/product/:id" element={<ProductDetailsPage />} />
          <Route path="/category/:mainCategorySlug" element={<MainCategoryPage />} />
          <Route path="/category/:mainCategorySlug/:collectionSlug" element={<ProductPage />} />
          <Route
            path="/collections/:collectionId/categories/:categoryId"
            element={<ProductPage />}
          />
          <Route path="/collections/:collectionId" element={<ProductPage />} />
          <Route path="/attars" element={<AttarsPage />} />
          <Route path="/attars/:slug" element={<AttarDetailPage />} />
          <Route path="/our-story" element={<OurStory />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/reviews" element={<Review />} />

          <Route path="/order-success" element={<OrderSuccess />} />
          <Route path="/my-orders" element={<MyOrders />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/contact" element={<ContactForm />} />
          <Route path="/track-order" element={<TrackOrder />} />
          <Route path="/shipping" element={<ShippingInfo />} />
          <Route path="/returns-exchanges" element={<ReturnsExchanges />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />

          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<EmailVerify />} />
        </Routes>
      </Suspense>
      <Footer />
    </>
  );
};

export default App;
